import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/galleryApi";

const selectCls = "w-full bg-[#060B14] border border-[#1A2332] text-white rounded-md px-3 h-10 text-sm";

export function CreateGalleryDialog({ open, onOpenChange, isAdmin, scopeOrgIds, onCreated }) {
  const [title, setTitle] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isAdmin) api.get("/event/admin/organizations").then((o) => setOrgs((Array.isArray(o) ? o : o.organizations || []).filter((x) => !scopeOrgIds || scopeOrgIds.includes(x.id)))).catch(() => {});
    api.get("/event/events").then((d) => setEvents(Array.isArray(d) ? d : d.events || [])).catch(() => {});
  }, [open, isAdmin, scopeOrgIds]);

  const visibleEvents = isAdmin ? events.filter((e) => e.org_id === orgId) : events;

  const submit = async () => {
    setBusy(true);
    try {
      const g = await api.post("/galleries", { title: title.trim(), event_id: eventId || null, org_id: orgId || undefined });
      toast.success(`Galerie « ${g.title} » creee`);
      onCreated(g);
      setTitle(""); setEventId("");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white" data-testid="create-gallery-modal">
        <DialogHeader><DialogTitle>Nouvelle galerie</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#94A3B8] mb-1.5 block">Titre</label>
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Congres Diaspora 2026 — Soiree de gala"
              className="bg-[#060B14] border-[#1A2332] text-white" data-testid="new-gallery-title" onKeyDown={(e) => e.key === "Enter" && title.trim() && submit()} />
          </div>
          {isAdmin && (
            <div>
              <label className="text-xs text-[#94A3B8] mb-1.5 block">Organisation</label>
              <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setEventId(""); }} className={selectCls} data-testid="new-gallery-org">
                <option value="">Choisir une organisation</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.org_name || o.name || o.id}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-[#94A3B8] mb-1.5 block">Evenement associe (requis pour une galerie reservee aux participants)</label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={selectCls} data-testid="new-gallery-event">
              <option value="">Aucun evenement</option>
              {visibleEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
            </select>
          </div>
          <p className="text-xs text-[#64748B]">La galerie est creee en mode <b className="text-[#94A3B8]">privee</b>. Vous choisirez le mode de partage apres l'import et le traitement des photos.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#1A2332] text-[#94A3B8] bg-transparent">Annuler</Button>
          <Button onClick={submit} disabled={busy || !title.trim() || (isAdmin && !orgId)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="confirm-create-gallery">Creer la galerie</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
